// shared/chainLogic.js
// アンサーチェーン専用のゲームロジック

import {
  CHAIN_SETTINGS,
  CHAIN_PHASE,
  normalizeChainAnswer,
  generateChainTurnOrder,
} from './fiveLeague';

/**
 * チェーン回答の判定結果
 */
export const CHAIN_RESULT = {
  CORRECT: 'correct',       // 正解
  WRONG: 'wrong',           // 不正解
  DUPLICATE: 'duplicate',   // 重複
};

/**
 * 回答を判定する
 * @param {string} input - ユーザー入力
 * @param {Array<{text: string, reading: string}>} answers - 正解リスト
 * @param {Array<{reading: string}>} submittedAnswers - 既に回答済みのリスト
 * @returns {{result: string, matchedAnswer: object|null}}
 */
export function judgeChainAnswer(input, answers, submittedAnswers = []) {
  const normalizedInput = normalizeChainAnswer(input);

  if (!normalizedInput) {
    return { result: CHAIN_RESULT.WRONG, matchedAnswer: null };
  }

  // 重複チェック（読みで比較）
  const isDuplicate = submittedAnswers.some(
    submitted => normalizeChainAnswer(submitted.reading) === normalizedInput
  );

  if (isDuplicate) {
    return { result: CHAIN_RESULT.DUPLICATE, matchedAnswer: null };
  }

  // 正解チェック（読みで比較）
  const matchedAnswer = answers.find(
    answer => normalizeChainAnswer(answer.reading) === normalizedInput
  );

  if (matchedAnswer) {
    return { result: CHAIN_RESULT.CORRECT, matchedAnswer };
  }

  return { result: CHAIN_RESULT.WRONG, matchedAnswer: null };
}

/**
 * 次のターンのプレイヤースロットを取得
 * @param {number[]} turnOrder - ターン順配列
 * @param {number} currentTurnIndex - 現在のターンインデックス
 * @returns {number|null} - 次のプレイヤースロット、または終了の場合null
 */
export function getNextTurnPlayerSlot(turnOrder, currentTurnIndex) {
  const nextIndex = currentTurnIndex + 1;
  if (nextIndex >= turnOrder.length) {
    return null; // 全ターン終了
  }
  return turnOrder[nextIndex];
}

/**
 * ゲーム状態を初期化
 * @param {Object} question - 問題データ
 * @param {string[]} playerOrder - プレイヤーIDの順番
 * @param {number} answerCount - 回答数（5 or 10）
 * @returns {Object} - 初期ゲーム状態
 */
export function initChainGameState(question, playerOrder, answerCount) {
  const playerCount = playerOrder.length;
  const turnOrder = generateChainTurnOrder(playerCount, answerCount);
  const timeLimit = answerCount === 10
    ? CHAIN_SETTINGS.TIME_LIMIT_10
    : CHAIN_SETTINGS.TIME_LIMIT_5;

  const now = Date.now();

  return {
    mode: 'chain',
    phase: CHAIN_PHASE.READING,
    questionId: question.id,
    prompt: question.prompt,
    answers: question.answers, // 正解リスト（クライアントには送らない）
    answerCount,
    turnOrder,
    currentTurnIndex: 0,
    currentPlayerSlot: turnOrder[0],
    submittedAnswers: [],
    startedAt: null, // READINGフェーズ終了時にセット
    endsAt: null,
    timeLimit,
    correctCount: 0,
  };
}

/**
 * 回答フェーズを開始
 * @param {Object} gameState - 現在のゲーム状態
 * @returns {Object} - 更新されたゲーム状態
 */
export function startAnsweringPhase(gameState) {
  const now = Date.now();
  return {
    ...gameState,
    phase: CHAIN_PHASE.ANSWERING,
    startedAt: now,
    endsAt: now + gameState.timeLimit * 1000,
  };
}

/**
 * 正解時の状態更新
 * @param {Object} gameState - 現在のゲーム状態
 * @param {Object} matchedAnswer - マッチした回答
 * @param {number} playerSlot - 回答したプレイヤーのスロット
 * @returns {Object} - 更新されたゲーム状態
 */
export function handleCorrectAnswer(gameState, matchedAnswer, playerSlot) {
  const submittedAnswers = [
    ...gameState.submittedAnswers,
    {
      playerSlot,
      text: matchedAnswer.text,
      reading: matchedAnswer.reading,
      correct: true,
      answeredAt: Date.now(),
    },
  ];

  const nextTurnIndex = gameState.currentTurnIndex + 1;
  const isComplete = nextTurnIndex >= gameState.answerCount;

  return {
    ...gameState,
    submittedAnswers,
    currentTurnIndex: nextTurnIndex,
    currentPlayerSlot: isComplete ? null : gameState.turnOrder[nextTurnIndex],
    correctCount: gameState.correctCount + 1,
    phase: isComplete ? CHAIN_PHASE.COMPLETE : CHAIN_PHASE.CORRECT,
  };
}

/**
 * 不正解時の状態更新（同じプレイヤーが継続）
 * @param {Object} gameState - 現在のゲーム状態
 * @param {string} input - 入力テキスト
 * @param {number} playerSlot - 回答したプレイヤーのスロット
 * @param {string} reason - 'wrong' or 'duplicate'
 * @returns {Object} - 更新されたゲーム状態
 */
export function handleWrongAnswer(gameState, input, playerSlot, reason) {
  // 不正解でも同じプレイヤーが継続（パス不可）
  return {
    ...gameState,
    phase: CHAIN_PHASE.WRONG,
    lastWrongInput: input,
    lastWrongReason: reason,
    lastWrongPlayerSlot: playerSlot,
  };
}

/**
 * タイムアウト時の状態更新
 * @param {Object} gameState - 現在のゲーム状態
 * @returns {Object} - 更新されたゲーム状態
 */
export function handleTimeout(gameState) {
  return {
    ...gameState,
    phase: CHAIN_PHASE.TIMEOUT,
  };
}

/**
 * 結果フェーズへ移行
 * @param {Object} gameState - 現在のゲーム状態
 * @returns {Object} - 更新されたゲーム状態
 */
export function transitionToResult(gameState) {
  return {
    ...gameState,
    phase: CHAIN_PHASE.RESULT,
  };
}

/**
 * 残り時間を計算
 * @param {number} endsAt - 終了時刻のタイムスタンプ
 * @returns {number} - 残り秒数（0以上）
 */
export function getRemainingTime(endsAt) {
  if (!endsAt) return 0;
  return Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
}

/**
 * プレイヤースロットからプレイヤー情報を取得
 * @param {number} slot - スロットインデックス
 * @param {string[]} playerOrder - プレイヤーID配列
 * @param {Object[]} players - プレイヤー情報配列
 * @returns {Object|null} - プレイヤー情報
 */
export function getPlayerBySlot(slot, playerOrder, players) {
  const playerId = playerOrder[slot];
  if (!playerId) return null;
  return players.find(p => p.playerId === playerId) || null;
}

/**
 * クライアント用のゲーム状態を生成（正解リストを除外）
 * @param {Object} gameState - サーバー側のゲーム状態
 * @returns {Object} - クライアント用の状態
 */
export function getClientGameState(gameState) {
  const { answers, ...clientState } = gameState;
  return {
    ...clientState,
    // 正解の読みのみ送信（表示用、漢字は隠す）
    // ただしCOMPLETE or RESULT時は全て公開
    answersForDisplay: gameState.phase === CHAIN_PHASE.COMPLETE ||
                       gameState.phase === CHAIN_PHASE.RESULT
      ? answers
      : null,
  };
}
